import { type CustomTooltipProps } from "ag-grid-react"
import { useMemo } from "react"
import { addDays } from "date-fns"

import { Card, CardContent } from "~/components/reusable/Card"
import { Button } from "~/components/reusable/Button"

import { formatDate, getStartOfCurrentWeekUTC } from "~/utils/datetime"
import { formatSkuId } from "~/utils/helpers"
import { filterDefined } from "~/utils/tsUtils"

import { type StockOutInformationType } from "~/utils/outOfStockDatesUtils"
import { useGetCollections } from "~/hooks/useCollections"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import { max } from "lodash"
import { useFlagsActionDialogStore } from "~/stores/flags-action-dialog-store"

interface FlagsTooltipValue {
  flagMessage?: string
  variants?: IVariantSummary[]
  outOfStockDates: StockOutInformationType[]
  outOfStockDatesFashion: StockOutInformationType[]
}

interface FlagsTooltipProps extends CustomTooltipProps {
  title: string
  value: FlagsTooltipValue
}

const StatusSection = ({ flagMessage }: { flagMessage?: string }) => {
  if (!flagMessage) return null

  return (
    <div>
      <div className="text-nowrap font-bold">Status</div>
      <div>{flagMessage}</div>
    </div>
  )
}

const StockoutTypeSection = ({
  items,
  count,
  total,
  label,
  statusFilter,
}: {
  items: StockOutInformationType[]
  count: number
  total: number
  label: string
  statusFilter: string
}) => {
  if (count <= 0) return null

  return (
    <>
      <div className="mb-1 font-bold text-xs">
        {count === total
          ? `All ${label} SKUs are ${statusFilter === "stockout" ? "out of stock" : "should be reordered now"}`
          : `${count} out of ${total} ${label} SKUs are ${statusFilter === "stockout" ? "out of stock" : "should be reordered now"}`}
      </div>
      {items
        .filter((s) => s?.status === statusFilter)
        .map((s) => (
          <p className="text-[10px] text-nowrap" key={s.skuId}>
            - {formatSkuId(s?.skuId)} ({formatDate(s?.stockoutDates[0])})
          </p>
        ))}
    </>
  )
}

const StockoutsSection = ({
  outOfStockCount,
  outOfStockDates,
  outOfStockCountFashion,
  outOfStockDatesFashion,
  shouldReorderNowCount,
}: {
  outOfStockCount: number
  outOfStockDates: StockOutInformationType[]
  outOfStockCountFashion: number
  outOfStockDatesFashion: StockOutInformationType[]
  shouldReorderNowCount: number
}) => {
  const hasStockoutInfo =
    outOfStockCount > 0 || outOfStockCountFashion > 0 || shouldReorderNowCount > 0

  if (!hasStockoutInfo) return null

  return (
    <div className="flex flex-col gap-2 max-w-[550px]">
      <p className="text-nowrap font-bold">Stockouts information</p>
      <div className="flex flex-row gap-2">
        {(outOfStockCount > 0 || outOfStockCountFashion > 0) && (
          <div className="flex flex-col max-h-[600px] overflow-y-auto">
            {outOfStockCount > 0 && (
              <StockoutTypeSection
                items={outOfStockDates}
                count={outOfStockCount}
                total={outOfStockDates.length}
                label="NOOS"
                statusFilter="stockout"
              />
            )}

            {outOfStockCountFashion > 0 && (
              <div className={outOfStockCount > 0 ? "mt-2" : ""}>
                <StockoutTypeSection
                  items={outOfStockDatesFashion}
                  count={outOfStockCountFashion}
                  total={outOfStockDatesFashion?.length}
                  label="Fashion"
                  statusFilter="stockout"
                />
              </div>
            )}
          </div>
        )}

        {shouldReorderNowCount > 0 && (
          <div className="flex flex-col max-h-[600px] overflow-y-auto">
            <StockoutTypeSection
              items={outOfStockDates}
              count={shouldReorderNowCount}
              total={outOfStockDates.length}
              label=""
              statusFilter="reorder"
            />
          </div>
        )}
      </div>
    </div>
  )
}

const WarningsSection = ({ variants }: { variants: IVariantSummary[] }) => {
  const openDialog = useFlagsActionDialogStore((s) => s.openDialog)

  if (variants.length === 0) return null

  return (
    <div className="flex flex-col gap-2 max-w-[200px]">
      <p className="font-bold">Warning about demand on SKUs</p>
      <p>
        We recommend that you either update the expected start date or deactivate the SKUs. This can
        be done by using the button below, or removing them from the collection.
      </p>
      <Button variant="neutral" onClick={() => openDialog(variants)}>
        Manage SKUs
      </Button>
    </div>
  )
}

const FlagsTooltip = (props: FlagsTooltipProps) => {
  const { data: collections } = useGetCollections()

  const flagMessage = props.value.flagMessage
  const variants = props.value.variants || []
  const outOfStockDates = props.value.outOfStockDates || []
  const outOfStockDatesFashion = props.value.outOfStockDatesFashion || []

  const outOfStockCount = outOfStockDates.filter((s) => s?.status === "stockout").length
  const outOfStockCountFashion = outOfStockDatesFashion.filter(
    (s) => s?.status === "stockout",
  ).length
  const shouldReorderNowCount = outOfStockDates.filter((s) => s?.status === "reorder").length

  const variantsWithWarnings = useMemo(() => {
    return (
      variants?.filter(
        (sku) =>
          !sku.soldSince &&
          sku.activatedFrom &&
          sku.forecastQuantity > 0 &&
          sku.incomingOrders.length === 0 &&
          sku.stockLevel == 0 &&
          (sku.settings.activatedFrom ??
            max(
              filterDefined([
                ...sku.collections.map(
                  (col) =>
                    collections
                      ?.filter((c) => !c.endDate || c.endDate > getStartOfCurrentWeekUTC())
                      .find((c) => c.id === Number(col.id))?.startDate,
                ),
              ]),
            ) ??
            getStartOfCurrentWeekUTC()) < addDays(getStartOfCurrentWeekUTC(), -14),
      ) ?? []
    )
  }, [variants, collections])

  const shouldShowTooltip =
    props.location === "cell" &&
    (flagMessage !== undefined ||
      outOfStockCount > 0 ||
      outOfStockCountFashion > 0 ||
      shouldReorderNowCount > 0 ||
      variantsWithWarnings.length > 0)

  if (!shouldShowTooltip) return null

  return (
    <Card className="max-w-[700px]">
      <CardContent>
        <div className="flex flex-row gap-3">
          <div className="flex flex-col gap-2">
            <StatusSection flagMessage={flagMessage} />
          </div>

          <StockoutsSection
            outOfStockCount={outOfStockCount}
            outOfStockDates={outOfStockDates}
            outOfStockCountFashion={outOfStockCountFashion}
            outOfStockDatesFashion={outOfStockDatesFashion}
            shouldReorderNowCount={shouldReorderNowCount}
          />

          <WarningsSection variants={variantsWithWarnings} />
        </div>
      </CardContent>
    </Card>
  )
}

export default FlagsTooltip
